import React, { useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Modal,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import { useAppTheme } from '../../utils/useAppTheme';

let verseLookupPromise = null;
const loadVerseLookup = async () => {
  if (!verseLookupPromise) {
    verseLookupPromise = import('../../data/bibleVerseLookup.json')
      .then(module => module.default || module);
  }
  return verseLookupPromise;
};

export default function WebVerseReference({ reference, style, label }) {
  const { colors } = useAppTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const [visible, setVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verseText, setVerseText] = useState(null);

  if (!reference) return null;

  const open = async event => {
    event?.stopPropagation?.();
    setVisible(true);
    setLoading(true);
    try {
      const lookup = await loadVerseLookup();
      setVerseText(lookup[reference] || null);
    } catch (error) {
      console.error('Unable to load KJV verse lookup:', error);
      setVerseText(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <TouchableOpacity
        style={style}
        onPress={open}
        accessibilityRole="button"
        accessibilityLabel={`Open ${reference} in the KJV Bible`}
      >
        <Text style={styles.reference}>{label || reference}</Text>
      </TouchableOpacity>
      <Modal visible={visible} transparent animationType="fade" onRequestClose={() => setVisible(false)}>
        <TouchableOpacity style={styles.backdrop} activeOpacity={1} onPress={() => setVisible(false)}>
          <View style={styles.dialog}>
            <TouchableOpacity activeOpacity={1} onPress={event => event?.stopPropagation?.()}>
              <View style={styles.header}>
                <Text style={styles.title}>📖 {reference}</Text>
                <TouchableOpacity style={styles.close} onPress={() => setVisible(false)}>
                  <Text style={styles.closeText}>×</Text>
                </TouchableOpacity>
              </View>
              <ScrollView style={styles.body} contentContainerStyle={styles.bodyContent}>
                {loading ? (
                  <ActivityIndicator size="large" color={colors.primary} />
                ) : verseText ? (
                  <Text style={styles.verse}>“{verseText}”</Text>
                ) : (
                  <Text style={styles.notFound}>Verse text not available.</Text>
                )}
                {!loading && <Text style={styles.version}>— KJV</Text>}
              </ScrollView>
            </TouchableOpacity>
          </View>
        </TouchableOpacity>
      </Modal>
    </>
  );
}

const createStyles = colors => StyleSheet.create({
  reference: {
    color: colors.primary, textDecorationLine: 'underline', fontWeight: '600',
    fontSize: 12, cursor: 'pointer',
  },
  backdrop: {
    flex: 1, backgroundColor: 'rgba(0,0,0,0.55)', alignItems: 'center',
    justifyContent: 'center', padding: 16,
  },
  dialog: {
    width: '100%', maxWidth: 520, backgroundColor: colors.surface,
    borderRadius: 14, overflow: 'hidden', borderWidth: 1, borderColor: colors.border,
  },
  header: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
    padding: 18, backgroundColor: colors.primarySurface, borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  title: { color: colors.text, fontSize: 17, fontWeight: '700' },
  close: { paddingHorizontal: 8, paddingVertical: 2, cursor: 'pointer' },
  closeText: { color: colors.textSecondary, fontSize: 28, lineHeight: 28 },
  body: { maxHeight: '65vh' },
  bodyContent: { padding: 22, minHeight: 120, justifyContent: 'center' },
  verse: { color: colors.text, fontSize: 17, lineHeight: 28, fontStyle: 'italic' },
  notFound: { color: colors.textSecondary, fontSize: 14, textAlign: 'center' },
  version: { color: colors.textSecondary, fontSize: 12, textAlign: 'right', marginTop: 14 },
});